import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  IconSearch,
  IconReport,
  IconCopy,
  IconFileTypePdf,
  IconLoader2,
} from '@tabler/icons-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { formatDateTime } from '../utils/dateFormat';

interface RiskScoreRecord {
  id: number;
  risk_score: number | null;
  customer_identifier: string;
  phone_number: string;
  basket_id: string;
  store_id: string;
  promo_data_allotted: number | null;
  matched_reward_rules: string | null;
  matched_risk_signals: string | null;
  matched_risk_descriptions: string | null;
  basket_timestamp: string;
  created_at: string;
}

interface ReportsResponse {
  sparUuid: string;
  records: RiskScoreRecord[];
}

interface GenerateReportResponse {
  sparUuid: string;
  report: string;
  basketCount: number;
  riskRecordCount: number;
}

function riskBadgeVariant(score: number | null): 'destructive' | 'secondary' | 'default' | 'outline' {
  if (score === null) return 'outline';
  if (score >= 70) return 'destructive';
  if (score >= 40) return 'secondary';
  return 'default';
}

function truncate(value: string | null, max = 40): string {
  if (!value) return '—';
  return value.length > max ? value.slice(0, max) + '…' : value;
}

export function AbuseDetectionReportsPage() {
  const [msisdn, setMsisdn] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReportsResponse | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<GenerateReportResponse | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const reportContentRef = useRef<HTMLDivElement>(null);

  const handleSearch = async () => {
    const query = msisdn.trim();
    if (!query) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setReportData(null);
    setReportError(null);
    setPage(0);
    try {
      const res = await api.get<ReportsResponse>(
        `/spar-mobile/abuse-detection-reports?msisdn=${encodeURIComponent(query)}`,
      );
      setResult(res.data);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to load reports';
      setError(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!result?.sparUuid) return;
    setReportLoading(true);
    setReportError(null);
    setReportData(null);
    try {
      const res = await api.post<GenerateReportResponse>(
        '/spar-mobile/abuse-detection-reports/generate-report',
        { sparUuid: result.sparUuid },
      );
      setReportData(res.data);
      setReportOpen(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to generate report';
      setReportError(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setReportLoading(false);
    }
  };

  const handleCopyReport = async () => {
    if (!reportData?.report) return;
    await navigator.clipboard.writeText(reportData.report);
    toast.success('Report copied to clipboard');
  };

  const handleDownloadPdf = async () => {
    if (!reportContentRef.current || !reportData) return;
    setPdfLoading(true);
    try {
      const canvas = await html2canvas(reportContentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      let yOffset = 0;
      while (yOffset < imgHeight) {
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, margin - yOffset, contentWidth, imgHeight);
        yOffset += pageHeight - margin * 2;
      }

      const filename = `abuse-report-${reportData.sparUuid.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);
    } finally {
      setPdfLoading(false);
    }
  };

  const totalRecords = result?.records.length ?? 0;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const paginated = result?.records.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ?? [];

  return (
    <TooltipProvider>
      <div>
        <PageHeader title="Abuse Detection Reports" />
        <MainCard>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="w-[260px]">
              <Input
                placeholder="e.g. 27821234567"
                value={msisdn}
                onChange={(e) => setMsisdn(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={loading || !msisdn.trim()}
            >
              {loading ? <IconLoader2 size={16} className="animate-spin" /> : <IconSearch size={16} />}
              Search
            </Button>
            <Button
              variant="secondary"
              onClick={handleGenerateReport}
              disabled={reportLoading || !result?.sparUuid || result.records.length === 0}
            >
              {reportLoading ? <IconLoader2 size={16} className="animate-spin" /> : <IconReport size={16} />}
              {reportLoading ? 'Generating Report...' : 'Generate AI Report'}
            </Button>
            {reportLoading && (
              <span className="text-sm text-muted-foreground">
                Fetching basket data and generating analysis — this may take 10-15 seconds...
              </span>
            )}
          </div>

          {error && (
            <Alert variant="destructive" className="mb-3">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm text-muted-foreground">SPAR UUID:</span>
                <span className="text-sm font-mono">{result.sparUuid}</span>
                <span className="text-sm text-muted-foreground ml-3">
                  {result.records.length} record{result.records.length !== 1 ? 's' : ''} found
                </span>
              </div>

              {result.records.length === 0 ? (
                <Alert>
                  <AlertDescription>No risk score history found for this customer.</AlertDescription>
                </Alert>
              ) : (
                <>
                  <Card className="border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Basket Timestamp</TableHead>
                          <TableHead>Risk Score</TableHead>
                          <TableHead>Basket ID</TableHead>
                          <TableHead>Store ID</TableHead>
                          <TableHead className="text-right">Promo Data (MB)</TableHead>
                          <TableHead>Risk Signals</TableHead>
                          <TableHead>Risk Descriptions</TableHead>
                          <TableHead>Reward Rules</TableHead>
                          <TableHead>Created At</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginated.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell className="whitespace-nowrap">
                              {formatDateTime(row.basket_timestamp)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={riskBadgeVariant(row.risk_score)}>
                                {row.risk_score ?? 'N/A'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{row.basket_id}</TableCell>
                            <TableCell>{row.store_id}</TableCell>
                            <TableCell className="text-right">{row.promo_data_allotted ?? '—'}</TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger render={<span />}>
                                  {truncate(row.matched_risk_signals)}
                                </TooltipTrigger>
                                <TooltipContent>{row.matched_risk_signals ?? ''}</TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger render={<span />}>
                                  {truncate(row.matched_risk_descriptions)}
                                </TooltipTrigger>
                                <TooltipContent>{row.matched_risk_descriptions ?? ''}</TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger render={<span />}>
                                  {truncate(row.matched_reward_rules)}
                                </TooltipTrigger>
                                <TooltipContent>{row.matched_reward_rules ?? ''}</TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {formatDateTime(row.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Rows per page:</span>
                        <Select
                          value={String(rowsPerPage)}
                          onValueChange={(val) => {
                            if (val) {
                              setRowsPerPage(parseInt(val, 10));
                              setPage(0);
                            }
                          }}
                        >
                          <SelectTrigger className="w-[70px]" size="sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[25, 50, 100].map((opt) => (
                              <SelectItem key={opt} value={String(opt)}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>
                          {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, totalRecords)} of {totalRecords}
                        </span>
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                            Prev
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
                            Next
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {reportError && (
                    <Alert variant="destructive" className="mt-3">
                      <AlertDescription>{reportError}</AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </MainCard>

        <Dialog
          open={reportOpen}
          onOpenChange={(open) => { if (!open) setReportOpen(false); }}
        >
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3 flex-wrap">
                <DialogTitle>Abuse Detection Report</DialogTitle>
                {reportData && (
                  <>
                    <Badge variant="destructive" className="text-xs">
                      {reportData.riskRecordCount} risk records
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {reportData.basketCount} baskets analysed
                    </Badge>
                  </>
                )}
              </div>
            </DialogHeader>

            {reportData && (
              <div
                ref={reportContentRef}
                className="prose prose-sm max-w-none dark:prose-invert [&_table]:w-full [&_table]:border-collapse [&_table]:mb-4 [&_th]:border [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-sm [&_th]:font-semibold [&_th]:bg-muted/50 [&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-1.5 [&_td]:text-sm [&_blockquote]:border-l-3 [&_blockquote]:border-amber-500 [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground"
              >
                <ReactMarkdown>{reportData.report}</ReactMarkdown>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleCopyReport}>
                <IconCopy size={14} />
                Copy Text
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
              >
                {pdfLoading ? <IconLoader2 size={14} className="animate-spin" /> : <IconFileTypePdf size={14} />}
                {pdfLoading ? 'Generating PDF...' : 'Download PDF'}
              </Button>
              <Button variant="outline" onClick={() => setReportOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
